import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { sendPasswordResetEmail } from '@core/auth/authApi';

/**
 * Forgot password page for auth.v2.meitre.com/forgot-password.
 *
 * User enters their email → POST /{lang}/reset-password/send-email
 * → shows success message. The backend sends an email with a reset link
 * pointing to the restaurant subdomain (/reset/{token}).
 */
export function ForgotPasswordPage() {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const restaurant = searchParams.get('restaurant') || '';

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // Preserve query params for back navigation
  const loginSearch = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return params.toString();
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setError('');
    setLoading(true);

    try {
      const lang = i18n.language || 'en';
      await sendPasswordResetEmail(lang, { username: email, restaurant });
      setSent(true);
    } catch {
      setError(t('PASSWORD_RESET_SEND_ERROR'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/login?${loginSearch}`);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-0 lg:p-4 bg-background">
      <div className="w-full lg:max-w-md bg-card shadow-none rounded-b-2xl lg:shadow-lg lg:rounded-2xl overflow-visible lg:overflow-hidden animate-fade-in border-t border-border/30">
        <main className="px-6 pt-8 pb-10 flex flex-col items-center">
          {sent ? (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
                <CheckCircle2 className="text-green-600" size={32} />
              </div>
              <h1 className="text-3xl font-display mb-2 text-center">
                {t('PASSWORD_RESET_EMAIL_SENT_TITLE')}
              </h1>
              <p className="text-foreground/50 text-sm mb-8 text-center max-w-xs">
                {t('PASSWORD_RESET_EMAIL_SENT_SUBTITLE')}
              </p>
              <p className="text-foreground font-medium text-sm mb-8 text-center">
                {email}
              </p>
              <div className="w-full max-w-xs">
                <Button
                  className="py-6 w-full transition-all duration-200 hover:scale-[1.02]"
                  onClick={handleBack}
                  type="button"
                >
                  {t('PASSWORD_RESET_BACK_TO_LOGIN')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-display mb-2 text-center">
                {t('PASSWORD_RESET_TITLE')}
              </h1>
              <p className="text-foreground/50 text-sm mb-8 text-center">
                {t('PASSWORD_RESET_SUBTITLE')}
              </p>

              <form
                className="w-full max-w-xs flex flex-col gap-4 mb-10"
                onSubmit={handleSubmit}
              >
                <Input
                  type="email"
                  placeholder={t('MAGIC_LINK_EMAIL_PLACEHOLDER')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  aria-label={t('SIGNUP_LABEL_EMAIL')}
                  className="py-6"
                />

                {error && (
                  <p className="text-red-500 text-sm text-center">{error}</p>
                )}

                <Button
                  type="submit"
                  className="py-6 transition-all duration-200 hover:scale-[1.02]"
                  disabled={loading || !email}
                >
                  {loading ? t('MAGIC_LINK_SENDING') : t('PASSWORD_RESET_SEND_LINK')}
                </Button>
              </form>
            </>
          )}
        </main>

        <footer className="border-t border-border px-6 py-4 flex items-center">
          <button
            aria-label="Go back"
            className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
            onClick={handleBack}
            type="button"
          >
            <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
          </button>
        </footer>
      </div>

      <p className="hidden lg:block mt-6 text-foreground/30 text-xs">
        Powered by{' '}
        <span
          className="font-['Playfair_Display'] font-bold tracking-wide"
          style={{ backgroundImage: 'linear-gradient(to right, #FF3366, #FF3366)', backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent' }}
        >
          Meitre
        </span>
      </p>
    </div>
  );
}

export default ForgotPasswordPage;
